// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { useEffect, useMemo, useState } from 'react';
import './Reports.css';
// import './reports-theme.css';
import AssessmentModal, { AssessmentReport } from './AssessmentModal';
import AllAssessmentsTab from './AllAssessmentsTab';
import { PAGE_SIZE, formatValue, filterReportsByDateRange, formatScore } from './Reportutils';
import {
  tableHeaderStyle,
  tableCellStyle,
  paginationBarStyle,
  pageButtonStyle,
  pageButtonDisabledStyle,
  tabBarStyle,
  tabStyleBase,
  tabActiveStyle,
  tabCloseStyle,
  filterBarStyle,
  fieldWrapStyle,
  fieldLabelStyle,
  dateInputStyle,
  clearFilterButtonStyle
} from './Reportstyles';

interface ReportUser {
  user_id?: number;
  user_name: string;
  email_id: string;
  assessment_count: number;
}

interface UserTab {
  emailId: string;
  userName: string;
  reports: AssessmentReport[];
  loading: boolean;
  error: string;
  reportsPage: number;
  dateFrom: string;
  dateTo: string;
}

// Content-only Reports view — no Sidebar, no auth-check.
// Meant to be rendered inside a page (e.g. OpenAiView) that already
// handles authentication and layout.
const ReportsContent = (): JSX.Element => {
  // --- Users tab state ---
  const [users, setUsers] = useState<ReportUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [page, setPage] = useState(1);

  // --- Tab bar state ---
  // 'users' and 'all' are fixed, always-present tabs. Per-user tabs are added dynamically.
  const [userTabs, setUserTabs] = useState<UserTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string>('all');

  // --- Modal state (shared by every tab) ---
  const [selectedReport, setSelectedReport] = useState<AssessmentReport | null>(null);

  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'Reports - AI Skill Assessment';

    return () => {
      document.title = originalTitle;
    };
  }, []);

  useEffect(() => {
    if (activeTabId !== 'users') return;

    const fetchReportUsers = async (): Promise<void> => {
      try {
        setUsersLoading(true);
        setUsersError('');

        const response = await fetch('/api/assessment/reports/users');

        if (!response.ok) {
          throw new Error('Failed to fetch report users');
        }

        const result = await response.json();
        setUsers(result.data || []);
      } catch (fetchError) {
        console.error('Error fetching report users:', fetchError);
        setUsersError('Unable to load users');
      } finally {
        setUsersLoading(false);
      }
    };

    fetchReportUsers();
  }, [activeTabId]);

  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return users.slice(start, start + PAGE_SIZE);
  }, [users, page]);

  const fetchReportsForUser = async (emailId: string): Promise<AssessmentReport[]> => {
    const response = await fetch(`/api/assessment/reports?email_id=${encodeURIComponent(emailId)}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch reports: ${response.status}`);
    }

    const result = await response.json();
    return result.data || [];
  };

  const openUserTab = async (user: ReportUser): Promise<void> => {
    const existingTab = userTabs.find((tab) => tab.emailId === user.email_id);

    if (existingTab) {
      setActiveTabId(user.email_id);
      return;
    }

    const newTab: UserTab = {
      emailId: user.email_id,
      userName: user.user_name,
      reports: [],
      loading: true,
      error: '',
      reportsPage: 1,
      dateFrom: '',
      dateTo: ''
    };

    setUserTabs((current) => [...current, newTab]);
    setActiveTabId(user.email_id);

    try {
      const reports = await fetchReportsForUser(user.email_id);
      setUserTabs((current) =>
        current.map((tab) => (tab.emailId === user.email_id ? { ...tab, reports, loading: false } : tab))
      );
    } catch (fetchError) {
      console.error('[Reports] Error fetching user reports:', fetchError);
      setUserTabs((current) =>
        current.map((tab) =>
          tab.emailId === user.email_id ? { ...tab, loading: false, error: 'Unable to load assessment reports.' } : tab
        )
      );
    }
  };

  const closeUserTab = (emailId: string): void => {
    setUserTabs((current) => current.filter((tab) => tab.emailId !== emailId));

    if (activeTabId === emailId) {
      setActiveTabId('all');
    }
  };

  const activeUserTab = userTabs.find((tab) => tab.emailId === activeTabId) || null;

  const setReportsPageForActiveTab = (updater: (current: number) => number): void => {
    setUserTabs((current) =>
      current.map((tab) => (tab.emailId === activeTabId ? { ...tab, reportsPage: updater(tab.reportsPage) } : tab))
    );
  };

  const setDateRangeForActiveTab = (field: 'dateFrom' | 'dateTo', value: string): void => {
    setUserTabs((current) =>
      current.map((tab) => (tab.emailId === activeTabId ? { ...tab, [field]: value, reportsPage: 1 } : tab))
    );
  };

  const clearDateRangeForActiveTab = (): void => {
    setUserTabs((current) =>
      current.map((tab) => (tab.emailId === activeTabId ? { ...tab, dateFrom: '', dateTo: '', reportsPage: 1 } : tab))
    );
  };

  const activeUserFilteredReports = useMemo(() => {
    if (!activeUserTab) return [];
    return filterReportsByDateRange(activeUserTab.reports, activeUserTab.dateFrom, activeUserTab.dateTo);
  }, [activeUserTab]);

  const activeUserReportsTotalPages = Math.max(1, Math.ceil(activeUserFilteredReports.length / PAGE_SIZE));

  const pagedActiveUserReports = useMemo(() => {
    if (!activeUserTab) return [];
    const start = (activeUserTab.reportsPage - 1) * PAGE_SIZE;
    return activeUserFilteredReports.slice(start, start + PAGE_SIZE);
  }, [activeUserFilteredReports, activeUserTab]);

  const isDateFilterActive = !!(activeUserTab && (activeUserTab.dateFrom || activeUserTab.dateTo));

  return (
    <div className="reports-page-wrapper">
      <div style={tabBarStyle}>
        <div style={activeTabId === 'all' ? tabActiveStyle : tabStyleBase} onClick={() => setActiveTabId('all')}>
          All Assessments
        </div>

        <div style={activeTabId === 'users' ? tabActiveStyle : tabStyleBase} onClick={() => setActiveTabId('users')}>
          Users
        </div>

        {userTabs.map((tab) => (
          <div
            key={tab.emailId}
            style={activeTabId === tab.emailId ? tabActiveStyle : tabStyleBase}
            onClick={() => setActiveTabId(tab.emailId)}
          >
            {tab.userName || tab.emailId}
            <button
              type="button"
              style={tabCloseStyle}
              onClick={(event) => {
                event.stopPropagation();
                closeUserTab(tab.emailId);
              }}
              aria-label={`Close ${tab.userName}`}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {activeTabId === 'users' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h1 style={{ margin: 0 }}>Reports / Performance</h1>
            <span style={{ color: '#666' }}>
              {users.length} {users.length === 1 ? 'user' : 'users'}
            </span>
          </div>

          {usersLoading && <p>Loading users...</p>}
          {usersError && <p style={{ color: '#b00020' }}>{usersError}</p>}

          {!usersLoading && !usersError && (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>User Name</th>
                    <th style={tableHeaderStyle}>Email</th>
                    <th style={tableHeaderStyle}>Assessment Count</th>
                    <th style={tableHeaderStyle}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {pagedUsers.length > 0 ? (
                    pagedUsers.map((user) => (
                      <tr key={user.email_id}>
                        <td style={tableCellStyle}>{user.user_name}</td>
                        <td style={tableCellStyle}>{user.email_id}</td>
                        <td style={tableCellStyle}>{user.assessment_count}</td>
                        <td style={tableCellStyle}>
                          <button type="button" style={pageButtonStyle} onClick={() => openUserTab(user)}>
                            View Reports
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} style={{ padding: '20px', textAlign: 'center' }}>
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {users.length > PAGE_SIZE && (
                <div style={paginationBarStyle}>
                  <span style={{ color: '#666', fontSize: '13px' }}>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    style={page === 1 ? pageButtonDisabledStyle : pageButtonStyle}
                    disabled={page === 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    style={page === totalPages ? pageButtonDisabledStyle : pageButtonStyle}
                    disabled={page === totalPages}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTabId === 'all' && <AllAssessmentsTab onViewReport={setSelectedReport} />}

      {activeUserTab && (
        <div className="reports-section assessment-section">
          <div className="selected-user-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h2>Assessment Reports</h2>
                <p>
                  <strong>{activeUserTab.userName}</strong>
                  {' — '}
                  {activeUserTab.emailId}
                </p>
              </div>
              <span style={{ color: '#666' }}>
                {activeUserFilteredReports.length} {activeUserFilteredReports.length === 1 ? 'report' : 'reports'}
                {isDateFilterActive ? ` of ${activeUserTab.reports.length}` : ''}
              </span>
            </div>
          </div>

          {!activeUserTab.loading && !activeUserTab.error && activeUserTab.reports.length > 0 && (
            <div style={filterBarStyle}>
              <div style={fieldWrapStyle}>
                <label style={fieldLabelStyle} htmlFor="reports-date-from">
                  From
                </label>
                <input
                  id="reports-date-from"
                  type="date"
                  style={dateInputStyle}
                  value={activeUserTab.dateFrom}
                  max={activeUserTab.dateTo || undefined}
                  onChange={(event) => setDateRangeForActiveTab('dateFrom', event.target.value)}
                />
              </div>

              <div style={fieldWrapStyle}>
                <label style={fieldLabelStyle} htmlFor="reports-date-to">
                  To
                </label>
                <input
                  id="reports-date-to"
                  type="date"
                  style={dateInputStyle}
                  value={activeUserTab.dateTo}
                  min={activeUserTab.dateFrom || undefined}
                  onChange={(event) => setDateRangeForActiveTab('dateTo', event.target.value)}
                />
              </div>

              {isDateFilterActive && (
                <button type="button" style={clearFilterButtonStyle} onClick={clearDateRangeForActiveTab}>
                  Clear dates
                </button>
              )}
            </div>
          )}

          {activeUserTab.loading && (
            <div className="loading-container">
              <p>Loading assessment reports...</p>
            </div>
          )}

          {activeUserTab.error && (
            <div className="error-container">
              <p>{activeUserTab.error}</p>
            </div>
          )}

          {!activeUserTab.loading && !activeUserTab.error && activeUserTab.reports.length === 0 && (
            <div className="empty-container">
              <p>No assessment reports found for this user.</p>
            </div>
          )}

          {!activeUserTab.loading &&
            !activeUserTab.error &&
            activeUserTab.reports.length > 0 &&
            activeUserFilteredReports.length === 0 && (
              <div className="empty-container">
                <p>No assessment reports found in the selected date range.</p>
              </div>
            )}

          {!activeUserTab.loading && !activeUserTab.error && activeUserFilteredReports.length > 0 && (
            <>
              <div className="table-container">
                <table className="reports-table assessment-table">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Email</th>
                      <th>Functional Area</th>
                      <th>Expertise Level</th>
                      <th>Score</th>
                      <th>Assessed Level</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagedActiveUserReports.map((report, index) => (
                      <tr key={report.assessment_id}>
                        <td>{formatValue(index + 1)}</td>
                        <td>{formatValue(report.email_id)}</td>
                        <td>{formatValue(report.functional_area)}</td>
                        <td>{formatValue(report.expertise_level)}</td>
                        <td>{formatScore(report.overall_score)}</td>
                        <td>{formatValue(report.assessed_level)}</td>
                        <td>
                          <button
                            type="button"
                            className="view-report-button"
                            onClick={() => setSelectedReport(report)}
                          >
                            View Report
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {activeUserFilteredReports.length > PAGE_SIZE && (
                <div style={paginationBarStyle}>
                  <span style={{ color: '#666', fontSize: '13px' }}>
                    Page {activeUserTab.reportsPage} of {activeUserReportsTotalPages}
                  </span>
                  <button
                    type="button"
                    style={activeUserTab.reportsPage === 1 ? pageButtonDisabledStyle : pageButtonStyle}
                    disabled={activeUserTab.reportsPage === 1}
                    onClick={() => setReportsPageForActiveTab((current) => Math.max(1, current - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    style={
                      activeUserTab.reportsPage === activeUserReportsTotalPages
                        ? pageButtonDisabledStyle
                        : pageButtonStyle
                    }
                    disabled={activeUserTab.reportsPage === activeUserReportsTotalPages}
                    onClick={() =>
                      setReportsPageForActiveTab((current) => Math.min(activeUserReportsTotalPages, current + 1))
                    }
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {selectedReport && <AssessmentModal report={selectedReport} onClose={() => setSelectedReport(null)} />}
    </div>
  );
};

export default ReportsContent;
